import { useEffect, useRef, useState } from "react";
import type { Familiarity } from "@itspip/shared";
import { logger } from "@/lib/logger";
import type { CoachError, CoachSignals } from "@/hooks/use-coach";

/**
 * Collapse KR drafts to a newline-joined string of trimmed titles, dropping
 * empties. Used for first-draft capture, stream-end history, and reflection
 * final-draft text.
 */
export function getKrTitleLines(krs: ReadonlyArray<{ title: string }>): string {
  return krs
    .map((k) => k.title.trim())
    .filter((t) => t.length > 0)
    .join("\n");
}

/**
 * Fire `onCommit` exactly once each time the coach stream transitions from
 * streaming→done with non-empty feedback and no error. Guards against
 * re-commit of the same feedback body on incidental re-renders.
 */
export function useCommitOnStreamEnd(
  isStreaming: boolean,
  error: CoachError | null,
  feedback: string,
  onCommit: () => void,
): void {
  const prev = useRef(isStreaming);
  const committedFor = useRef<string>("");
  useEffect(() => {
    const finishedOk =
      prev.current && !isStreaming && !error && feedback.length > 0;
    if (finishedOk && committedFor.current !== feedback) {
      committedFor.current = feedback;
      onCommit();
    }
    prev.current = isStreaming;
  }, [isStreaming, error, feedback, onCommit]);
}

/**
 * Return the override to send on a per-session coach request, or undefined
 * when the wizard-level value matches (or is absent from) the user's record.
 * Keeps the server-authoritative path the default.
 */
export function resolveFamiliarityOverride(
  sessionValue: Familiarity | null,
  recordValue: Familiarity | undefined,
): Familiarity | undefined {
  if (!sessionValue) return undefined;
  if (sessionValue === recordValue) return undefined;
  return sessionValue;
}

/**
 * Emit metadata-only analytics for coach sentinels.
 * Never pass draft or reflection text here.
 */
export function logCoachSignals(
  entity: "objective" | "kr_set",
  signals: CoachSignals,
  round?: number,
): void {
  if (signals.modeRecalibrated) {
    logger.info("coach.mode_recalibrated", {
      entity,
      to: signals.modeRecalibrated,
      ...(round !== undefined ? { round } : {}),
    });
  }
  if (signals.readiness) {
    logger.info("coach.readiness", {
      entity,
      value: signals.readiness,
      ...(round !== undefined ? { round } : {}),
    });
  }
  if (signals.returnToObjective) {
    logger.info("coach.return_to_objective", { entity });
  }
}

interface UseGraduationArgs {
  reflectionSubmitted: boolean;
  markReflectionSubmitted: () => void;
  onAdvance: () => void | Promise<void>;
}

interface GraduationApi {
  reflectionOpen: boolean;
  /** Call when the user clicks the readiness CTA. */
  triggerGraduation: () => void;
  /** Call when the reflection has been saved (or server error acknowledged). */
  finishReflection: () => void;
  /** Call when the user explicitly skips reflection. */
  skipReflection: () => void;
}

/**
 * Encapsulates the capture-reflection-then-advance flow shared by the
 * Objective and KR steps. If reflection has already been submitted once for
 * this session, the CTA advances immediately.
 */
export function useGraduation(args: UseGraduationArgs): GraduationApi {
  const [reflectionOpen, setReflectionOpen] = useState(false);

  const triggerGraduation = () => {
    if (args.reflectionSubmitted) {
      void args.onAdvance();
      return;
    }
    setReflectionOpen(true);
  };

  const complete = () => {
    setReflectionOpen(false);
    args.markReflectionSubmitted();
    void args.onAdvance();
  };

  return {
    reflectionOpen,
    triggerGraduation,
    finishReflection: complete,
    skipReflection: complete,
  };
}
